import { useMemo } from 'react';
import { formatCurrencys } from '../../../service/utils/facturationUtils';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, Area, AreaChart
} from 'recharts';

const PriceEvolutionChart = ({ priceHistory = [] }) => {
  const chartData = useMemo(() => {
    if (!Array.isArray(priceHistory) || priceHistory.length === 0) {
      return [];
    }

    try {
      // Trier par date croissante pour le graphique
      const sorted = [...priceHistory].sort((a, b) => 
        new Date(a.datetime || a.lastUpdateDate) - new Date(b.datetime || b.lastUpdateDate)
      );

      return sorted
        .filter(item => item && (item.adjustedPrice !== undefined || item.price !== undefined))
        .map((item, index) => {
          const price = item.adjustedPrice ?? item.price ?? 0;
          
          let dateLabel = `Point ${index + 1}`;
          const dateStr = item.datetime || item.date || item.lastUpdateDate;
          
          if (dateStr) {
            try {
              const date = new Date(dateStr);
              dateLabel = date.toLocaleDateString('fr-FR', { 
                day: '2-digit', 
                month: 'short',
                year: 'numeric'
              });
            } catch (e) {
              console.warn('Erreur formatage date:', e);
            }
          }

          return {
            name: dateLabel,
            price: parseFloat(price),
            dateFull: dateStr || dateLabel,
            volume: item.volume || 0
          };
        })
        .filter(item => !isNaN(item.price) && item.price > 0);

    } catch (error) {
      console.error('Erreur transformation données:', error);
      return [];
    }
  }, [priceHistory]);

  if (!Array.isArray(chartData) || chartData.length === 0) {
    return (
      <div className="text-center p-4 border rounded bg-light">
        <div className="text-muted mb-2">
          <i className="fas fa-chart-line fa-2x opacity-50"></i>
        </div>
        <p className="text-muted mb-0">
          Aucune donnée disponible pour le graphique
        </p>
      </div>
    );
  }

  const prices = chartData.map(d => d.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const range = maxPrice - minPrice;
  
  // Calcul des limites avec marges adaptatives
  const margin = range > 0 ? range * 0.1 : maxPrice * 0.1;
  const yDomain = [
    Math.max(0, minPrice - margin),
    maxPrice + margin
  ];

  // Tooltip personnalisé
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border rounded shadow-sm p-3" style={{ minWidth: '200px' }}>
          <div className="fw-semibold text-primary mb-2">{data.name}</div>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span className="text-muted small">Prix:</span>
            <span className="fw-bold">{formatCurrencys(data.price)}</span>
          </div>
          {data.volume > 0 && (
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted small">Volume:</span>
              <span className="fw-semibold">{data.volume.toLocaleString()}</span>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // Calcul de la tendance
  const trend = chartData.length > 1 
    ? chartData[chartData.length - 1].price - chartData[0].price 
    : 0;
  const trendPercent = chartData.length > 1 && chartData[0].price > 0
    ? ((trend / chartData[0].price) * 100).toFixed(2)
    : 0;

  return (
    <div className="price-evolution-chart">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0 fw-semibold">
          <i className="fas fa-chart-area text-primary me-2"></i>
          Évolution du Prix
        </h6>
        {trend !== 0 && (
          <div className={`badge ${trend >= 0 ? 'bg-success' : 'bg-danger'} bg-opacity-10 border border-opacity-25`}
               style={{ color: trend >= 0 ? '#198754' : '#dc3545' }}>
            {trend >= 0 ? '↗' : '↘'} Tendance: {trend >= 0 ? '+' : ''}{trendPercent}%
          </div>
        )}
      </div>
      
      <div style={{ width: '100%', height: 350 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 20, bottom: 20 }}
          >
            <defs>
              <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickLine={false}
              axisLine={{ stroke: '#d1d5db' }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            
            <YAxis 
              domain={yDomain}
              tickFormatter={(value) => formatCurrencys(value)}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickLine={false}
              axisLine={{ stroke: '#d1d5db' }}
              width={90}
            />
            
            <Tooltip content={<CustomTooltip />} />
            
            <Legend 
              verticalAlign="top" 
              height={40}
              iconType="circle"
              formatter={() => 'Prix du titre'}
              wrapperStyle={{ paddingTop: '10px' }}
            />
            
            <Area
              type="monotone"
              dataKey="price"
              stroke="#3b82f6"
              strokeWidth={3}
              fill="url(#colorPrice)"
              activeDot={{ r: 6, fill: '#3b82f6' }}
              dot={{ r: 4, fill: '#3b82f6', strokeWidth: 2, stroke: '#fff' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      
      {/* Statistiques en dessous du graphique */}
      <div className="row mt-4 g-3">
        <div className="col-4">
          <div className="text-center p-3 bg-light rounded">
            <div className="small text-muted mb-1">Prix Initial</div>
            <div className="fw-bold text-primary">{formatCurrencys(chartData[0]?.price || 0)}</div>
          </div>
        </div>
        <div className="col-4">
          <div className="text-center p-3 bg-light rounded">
            <div className="small text-muted mb-1">Prix Actuel</div>
            <div className="fw-bold text-dark">{formatCurrencys(chartData[chartData.length - 1]?.price || 0)}</div>
          </div>
        </div>
        <div className="col-4">
          <div className="text-center p-3 bg-light rounded">
            <div className="small text-muted mb-1">Écart</div>
            <div className={`fw-bold ${trend >= 0 ? 'text-success' : 'text-danger'}`}>
              {trend >= 0 ? '+' : ''}{formatCurrencys(trend)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceEvolutionChart;